import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { ShieldCheck, Eye, FilePlus2, Ban } from "lucide-react";

const SCOPE_INFO = {
  leer: { icon: Eye, label: "Leer el expediente", desc: "Casos, hechos, documentos (texto), demandados y empresas. Solo lectura." },
  proponer: { icon: FilePlus2, label: "Proponer hechos y casos", desc: "Las propuestas quedan PENDIENTES hasta tu aprobación en la bandeja de Aprobaciones. Nunca modifica ni borra nada." },
};

export default function OAuthAutorizar() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const tx = params.get("tx");
  const [info, setInfo] = useState(null);
  const [user, setUser] = useState(null);
  const [estado, setEstado] = useState("cargando");
  const [procesando, setProcesando] = useState(false);
  const [error, setError] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const i = await api.get(`/oauth/consent/info?tx=${encodeURIComponent(tx)}`);
      setInfo(i.data);
    } catch {
      setEstado("tx_invalida");
      return;
    }
    try {
      const me = await api.get("/auth/me");
      setUser(me.data);
      setEstado(me.data.actuacion === "CEO" ? "listo" : "no_ceo");
    } catch {
      setEstado("sin_sesion");
    }
  }, [tx]);

  useEffect(() => {
    if (!tx) { setEstado("tx_invalida"); return; }
    cargar();
  }, [tx, cargar]);

  const irALogin = () => {
    localStorage.setItem("oauth_tx", tx);
    navigate("/login");
  };

  const decidir = async (aprobar) => {
    setProcesando(true);
    setError(null);
    try {
      const res = await api.post(`/oauth/consent/${aprobar ? "approve" : "deny"}`, { tx });
      window.location.href = res.data.redirect_url;
    } catch (e) {
      setError(e.response?.data?.detail || "Error procesando la autorización");
      setProcesando(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--bg)" }} data-testid="oauth-consent-page">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="h-6 w-6" style={{ color: "var(--primary)" }} />
            <CardTitle className="text-lg">Autorizar conector de IA</CardTitle>
          </div>
          <CardDescription className="text-xs">
            LegalStory · acceso de solo lectura y propuestas con aprobación humana
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {estado === "cargando" && <p className="text-sm">Cargando…</p>}

          {estado === "tx_invalida" && (
            <p className="text-sm text-red-600" data-testid="oauth-tx-invalida">
              Solicitud de autorización no encontrada o expirada. Vuelve a iniciar la conexión desde Claude.
            </p>
          )}

          {estado === "sin_sesion" && info && (
            <div className="space-y-3">
              <p className="text-sm"><b>{info.client_name}</b> solicita acceso a LegalStory. Inicia sesión para continuar.</p>
              <Button onClick={irALogin} className="w-full ls-btn-primary" data-testid="oauth-login-btn">
                Iniciar sesión en LegalStory
              </Button>
            </div>
          )}

          {estado === "no_ceo" && (
            <div className="flex items-start gap-2 text-sm text-red-600" data-testid="oauth-no-ceo">
              <Ban className="h-4 w-4 mt-0.5" />
              <p>Solo el CEO puede autorizar conectores de IA. Sesión actual: {user?.email}</p>
            </div>
          )}

          {estado === "listo" && info && (
            <div className="space-y-4">
              <p className="text-sm">
                <b>{info.client_name}</b> solicita conectarse a LegalStory con estos permisos:
              </p>
              <div className="space-y-2">
                {info.scopes.map((s) => {
                  const si = SCOPE_INFO[s];
                  if (!si) return null;
                  const Icon = si.icon;
                  return (
                    <div key={s} className="flex items-start gap-2 p-2 rounded-lg" style={{ background: "var(--bg)" }}>
                      <Icon className="h-4 w-4 mt-0.5" style={{ color: "var(--primary)" }} />
                      <div>
                        <p className="text-sm font-semibold">{si.label}</p>
                        <p className="text-xs" style={{ color: "var(--text-muted)" }}>{si.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-[11px]" style={{ color: "var(--text-muted)" }}>
                Sin permisos de edición ni borrado. Toda actividad queda en la auditoría MCP. Puedes revocar este conector cuando quieras desde Auditoría → MCP / Claves IA.
              </p>
              {error && <p className="text-xs text-red-600">{error}</p>}
              <div className="flex gap-2">
                <Button onClick={() => decidir(true)} disabled={procesando} className="flex-1 bg-green-600 hover:bg-green-700 text-white" data-testid="oauth-permitir-btn">
                  Permitir
                </Button>
                <Button onClick={() => decidir(false)} disabled={procesando} variant="outline" className="flex-1" data-testid="oauth-cancelar-btn">
                  Cancelar
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
